import { PUBLISHED_FRESHNESS_TTL_MS } from "@repo/shared/types/dataset-run-status";
import { nanoid } from "@repo/shared/utils/id";
import { generateKeyBetween } from "fractional-indexing";
import { toast } from "sonner";

import { trpc } from "~/libs/trpc.lib";
import { getRouter } from "~/router";
import { user$ } from "../auth/user.store";
import { isSnapshotOrigin } from "../board-snapshot/snapshot-origins";
import { getSnapshotPayloadContext } from "../board-snapshot/snapshot-payload-context";
import { firstDatasource$ } from "../datasources";
import { activeOrganization$ } from "../organizations/active-organization.store";
import { datasets$, datasetsLocal$ } from "./datasets.crud";
import {
  ensureRunPayload,
  RunPayloadError,
  type RunPayloadScope,
  type RunPayloadUrlResolver,
} from "./rift-dataset-payload.query";

/** Datasets currently in-flight for a background refresh (dedup guard). */
const refreshingDatasets = new Set<string>();

/** Structured result of a manual dataset payload refresh request. */
export type RefreshDatasetPayloadResult = Awaited<
  ReturnType<typeof trpc.datasets.refreshPayload.mutate>
>;

/** Clears the refresh guard for a dataset (call when a new run arrives). */
export const clearRefreshGuard = (datasetId: string) => {
  refreshingDatasets.delete(datasetId);
};

/**
 * The run id the UI should render against: the client-only `activeRunId` when
 * set (it lags the server `runId` while a new payload downloads),
 * otherwise the server `runId`. Reads are tracked, so Legend State
 * computeds that call this re-run when the active run is adopted.
 */
export const getActiveDatasetRunId = (
  datasetId: string,
): string | null | undefined =>
  datasetsLocal$[datasetId].activeRunId.get() ??
  datasets$[datasetId].runId.get();

/**
 * Ensures a dataset's published Arrow payload is loaded into DuckDB,
 * and triggers a background refresh if the data is stale. This is the
 * single entry point for any code that needs published dataset data.
 *
 * Pass an `AbortSignal` (e.g. from the board-payload lifecycle or from a
 * rift query) so the underlying GCS fetch can be cancelled when the caller
 * is no longer interested.
 */
type EnsureDatasetPayloadOptions = {
  signal?: AbortSignal;
  /** Explicit run id to load; falls back to the dataset's active run. */
  runId?: string | null;
  scope?: RunPayloadScope;
  urlResolver?: RunPayloadUrlResolver;
  /** Pre-fetched inline Arrow bytes to seed the cache without a GCS fetch. */
  inlineBytes?: Uint8Array;
};

const toEnsurePayloadOptions = (
  signalOrOptions?: AbortSignal | EnsureDatasetPayloadOptions,
): EnsureDatasetPayloadOptions =>
  signalOrOptions instanceof AbortSignal
    ? { signal: signalOrOptions }
    : (signalOrOptions ?? {});

/**
 * Canonical action-layer API for ensuring a dataset payload is ready in DuckDB.
 * Resolves the dataset's active run id and delegates network/cache
 * orchestration to `ensureRunPayload`, keeping stale published-data refresh
 * checks centralized for org-scoped callers.
 *
 * Callers that only hold a dataset id (rift queries, stat batches, board filter
 * options) pass no scope, so a dataset hydrated from a board snapshot falls back
 * to the read-only scope + resolver its mount registered. Without that, a
 * read-only viewer's on-demand loads would hit the org-scoped
 * `runs.getResultUrl`, which an anonymous or cross-org caller cannot use.
 */
export const ensureDatasetPayload = async (
  datasetId: string,
  signalOrOptions?: AbortSignal | EnsureDatasetPayloadOptions,
) => {
  const options = toEnsurePayloadOptions(signalOrOptions);
  const runId = options.runId ?? getActiveDatasetRunId(datasetId);
  if (!runId) {
    throw new RunPayloadError("expired", "Dataset has no current run", {});
  }
  const snapshotContext =
    options.scope || options.urlResolver
      ? undefined
      : getSnapshotPayloadContext(datasetId);
  const scope = options.scope ?? snapshotContext?.scope;
  await ensureRunPayload({
    runId,
    signal: options.signal,
    scope,
    urlResolver: options.urlResolver ?? snapshotContext?.urlResolver,
    inlineBytes: options.inlineBytes,
  });
  if ((scope ?? "org") === "org") {
    checkAndRefreshDataset(datasetId);
  }
};

/** If published data is stale (older than TTL), triggers a background publish. */
export const checkAndRefreshDataset = (datasetId: string) => {
  // Snapshot datasets are owned by a foreign org — the server-side public
  // payload route is the only place that may trigger a refresh.
  if (isSnapshotOrigin(datasetId)) return;
  if (refreshingDatasets.has(datasetId)) return;
  // A `FAILED` dataset's last publish hit an error a retry cannot fix (dropped
  // table, revoked grant, bad SQL) and its `fetchedAt` is frozen at the last
  // good run, so it reads as permanently stale — without this gate every view
  // re-queues the same doomed query. Fixing it and publishing manually flips
  // the dataset back to `IDLE`, which resumes auto-refresh.
  if (datasets$[datasetId].status.get() !== "IDLE") return;
  const publishedAt = datasets$[datasetId].fetchedAt.get();
  if (!publishedAt) return;
  const age = Date.now() - new Date(publishedAt).getTime();
  if (age <= PUBLISHED_FRESHNESS_TTL_MS) return;

  refreshingDatasets.add(datasetId);
  trpc.datasets.publish.mutate({ datasetId }).catch(() => {
    refreshingDatasets.delete(datasetId);
  });
};

const getLocalPayloadRefreshBlockedUntilMs = (datasetId: string): number =>
  datasetsLocal$[datasetId].payloadRefreshBlockedUntilMs.get() ?? 0;

const setLocalPayloadRefreshBlockedUntilMs = (
  datasetId: string,
  blockedUntilMs: number,
) => {
  datasetsLocal$[datasetId].payloadRefreshBlockedUntilMs.set(blockedUntilMs);
};

/**
 * Returns remaining local cooldown time for manual refresh requests.
 * The backend remains the source of truth for throttling.
 */
export const getPayloadRefreshRetryAfterMs = (
  datasetId: string,
  nowMs = Date.now(),
): number =>
  Math.max(0, getLocalPayloadRefreshBlockedUntilMs(datasetId) - nowMs);

/**
 * Safety window after which a stuck manual-refresh spinner is force-cleared.
 * The flag is normally cleared by the incoming `RUNNING`/`IDLE`/`FAILED`
 * broadcast; this guards against a broadcast that never arrives.
 */
const MANUAL_REFRESH_PENDING_TIMEOUT_MS = 30_000;

/** Per-dataset timers that force-clear a stuck manual-refresh pending flag. */
const manualRefreshTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Clears the client-only "manual refresh pending" flag and its safety timer. */
export const clearManualRefreshPending = (datasetId: string) => {
  const timer = manualRefreshTimers.get(datasetId);
  if (timer) {
    clearTimeout(timer);
    manualRefreshTimers.delete(datasetId);
  }
  datasetsLocal$[datasetId].manualRefreshPending.set(false);
};

/**
 * Marks a publish as triggered from this client, before the server has said so.
 * Used by both entry points — a manual refresh and a save.
 */
const setManualRefreshPending = (datasetId: string) => {
  datasetsLocal$[datasetId].manualRefreshPending.set(true);
  const existing = manualRefreshTimers.get(datasetId);
  if (existing) clearTimeout(existing);
  manualRefreshTimers.set(
    datasetId,
    setTimeout(
      () => clearManualRefreshPending(datasetId),
      MANUAL_REFRESH_PENDING_TIMEOUT_MS,
    ),
  );
};

/**
 * Manually triggers a published payload refresh for a dataset.
 * Scope is dataset-global: all blocks using this dataset converge on the
 * new payload through existing etag-driven invalidation.
 */
export const refreshDatasetPayload = async (
  datasetId: string,
): Promise<RefreshDatasetPayloadResult> => {
  const localRetryAfterMs = getPayloadRefreshRetryAfterMs(datasetId);
  if (localRetryAfterMs > 0) {
    return {
      status: "THROTTLED",
      datasetId,
      reason: "COOLDOWN",
      retryAfterMs: localRetryAfterMs,
      nextAllowedAt: new Date(Date.now() + localRetryAfterMs),
    };
  }

  // Show the block-header spinner immediately; the incoming status broadcast
  // (or the safety timeout) will clear it.
  setManualRefreshPending(datasetId);

  let result: RefreshDatasetPayloadResult;
  try {
    result = await trpc.datasets.refreshPayload.mutate({ datasetId });
  } catch (error) {
    clearManualRefreshPending(datasetId);
    throw error;
  }

  setLocalPayloadRefreshBlockedUntilMs(
    datasetId,
    result.nextAllowedAt.getTime(),
  );

  // A throttled request never enters `RUNNING`, so no status broadcast will
  // arrive to clear the flag — clear it now.
  if (result.status === "THROTTLED") {
    clearManualRefreshPending(datasetId);
  }

  return result;
};

/**
 * Save the dataset query text and trigger a background publish.
 *
 * The publish is fired server-side off the query change, so `RUNNING` only
 * arrives after the synced mutation plus an Inngest round trip. The pending
 * flag covers that gap, and the outcome is reported by the publish status
 * indicators rather than by this toast — a publish can fail minutes later.
 */
export const saveDataset = async (datasetId: string) => {
  const currentQuery = datasets$[datasetId].query.get();
  const newQuery = datasets$[datasetId].draftQuery.get();

  if (!newQuery || newQuery === currentQuery) return;

  datasets$[datasetId].query.set(newQuery);
  setManualRefreshPending(datasetId);

  toast.success("Publishing dataset...");
};

/**
 * Fractional-indexing order key that appends a new dataset after the last
 * private ("draft") dataset in the sidebar list.
 */
const nextPrivateDatasetOrder = (): string => {
  const draftDatasets = Object.values(datasets$.get() ?? {}).filter(
    (d) => d.private,
  );
  draftDatasets.sort((a, b) =>
    a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
  );
  const lastOrder = draftDatasets.at(-1)?.order ?? null;
  return generateKeyBetween(lastOrder, null);
};

/**
 * Writes a new dataset row into the store (which the Legend State CRUD sync
 * persists via `datasets.create`). The server queues a publish automatically
 * when `query` + `datasourceId` are both set.
 */
const insertDataset = (fields: {
  datasetId: string;
  datasourceId: string;
  name: string;
  query: string;
}) => {
  const organizationId = activeOrganization$.id.get() as string;
  const userId = user$.id.get();

  datasets$[fields.datasetId].set({
    id: fields.datasetId,
    name: fields.name,
    organizationId,
    datasourceId: fields.datasourceId,
    ownerId: userId,
    query: fields.query,
    draftQuery: fields.query,
    private: true,
    favorite: false,
    verified: false,
    verifiedBy: null,
    verifiedAt: null,
    avatar: null,
    columnsConfig: {},
    riftConfig: null,
    columns: {},
    runId: null,
    fetchedAt: null,
    rowCount: null,
    sizeBytes: null,
    folderId: null,
    order: nextPrivateDatasetOrder(),
    status: "IDLE",
    createdAt: undefined as any,
    updatedAt: undefined as any,
  });
};

export const createDataset = () => {
  const datasourceId = firstDatasource$.id.get() as string;
  const datasetId = nanoid();

  insertDataset({ datasetId, datasourceId, name: "", query: "" });

  const router = getRouter();
  router.navigate({ to: `/datasets/${datasetId}` });
};

/**
 * Creates a private dataset from a chat-emitted SQL snippet against an
 * explicit datasource, without navigating away. Returns the new dataset id so
 * the caller can offer a "View dataset" affordance. Backs the "Save as
 * dataset" action on chat SQL blocks.
 */
export const saveSqlAsDataset = ({
  sql,
  datasourceId,
}: {
  sql: string;
  datasourceId: string;
}): string => {
  const datasetId = nanoid();
  insertDataset({ datasetId, datasourceId, name: "", query: sql });
  return datasetId;
};

export const deleteDataset = (datasetId: string) => {
  const router = getRouter();
  const pathname = router.state.location.pathname;
  const isViewingDeletedDataset = pathname === `/datasets/${datasetId}`;

  if (isViewingDeletedDataset) {
    const remainingIds = Object.values(datasets$.get() ?? {})
      .map((d) => d.id)
      .filter((id) => id !== datasetId)
      .sort(
        (a, b) =>
          new Date(datasets$[b].updatedAt.get()).getTime() -
          new Date(datasets$[a].updatedAt.get()).getTime(),
      );

    const nextId = remainingIds[0];

    if (nextId) {
      // Navigate first, then delete after the URL commits so the page's
      // redirect-on-missing guard doesn't fire on the deleted dataset before
      // unmount.
      router
        .navigate({
          to: "/datasets/$datasetId",
          params: { datasetId: nextId },
        })
        .then(() => {
          datasets$[datasetId].delete();
        });
    } else {
      datasets$[datasetId].delete();
      createDataset();
    }
  } else {
    datasets$[datasetId].delete();
  }

  toast.success("Dataset deleted");
};

/**
 * Reorders a dataset to a new position within its section's sorted list.
 * Uses fractional-indexing to compute a key between the two neighbors.
 */
export const reorderDataset = (
  datasetId: string,
  newIndex: number,
  sortedIds: string[],
) => {
  const without = sortedIds.filter((id) => id !== datasetId);
  if (newIndex < 0 || newIndex > without.length) return;
  const prevOrder =
    newIndex === 0
      ? null
      : (datasets$[without[newIndex - 1]].order.get() ?? null);
  const nextOrder =
    newIndex >= without.length
      ? null
      : (datasets$[without[newIndex]].order.get() ?? null);
  const newOrder = generateKeyBetween(prevOrder, nextOrder);
  datasets$[datasetId].order.set(newOrder);
};
